import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addRepairPartRpc } from '@/services/repairs';
import { fetchAccessories } from '@/services/accessories';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { useAuth } from '@/context/AuthContext';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Loader2, Plus } from 'lucide-react';
import { Accessory } from '@/types/models';

interface AddRepairPartModalProps {
  repairId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const AddRepairPartModal: React.FC<AddRepairPartModalProps> = ({
  repairId,
  isOpen,
  onClose,
}) => {
  const { hasPermission } = useAuth();
  const canViewCosts = hasPermission('purchases');
  const queryClient = useQueryClient();

  const [partType, setPartType] = useState<'inventory' | 'custom'>('inventory');
  const [selectedAccessoryId, setSelectedAccessoryId] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [quantity, setQuantity] = useState<number>(1);
  const [unitCharge, setUnitCharge] = useState<number>(0);
  const [unitCost, setUnitCost] = useState<number>(0);
  const [accessorySearch, setAccessorySearch] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: accessoriesData } = useQuery({
    queryKey: ['accessories', 'repair-parts', accessorySearch],
    queryFn: () => fetchAccessories({ search: accessorySearch, page: 0 }),
    enabled: isOpen && partType === 'inventory',
  });
  const accessories = accessoriesData?.data ?? [];

  const handleSelectAccessory = (acc: Accessory) => {
    setSelectedAccessoryId(acc.id);
    setUnitCharge(acc.selling_price ?? 0);
    setUnitCost(acc.average_cost ?? 0);
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (quantity <= 0) throw new Error('Quantity must be at least 1.');
      if (partType === 'inventory' && !selectedAccessoryId) {
        throw new Error('Please select an accessory part from stock.');
      }
      if (partType === 'custom' && !customDescription.trim()) {
        throw new Error('Please provide a description for the custom part.');
      }

      await addRepairPartRpc({
        repair_id: repairId,
        accessory_id: partType === 'inventory' ? selectedAccessoryId : null,
        description: partType === 'custom' ? customDescription.trim() : null,
        quantity,
        unit_cost: canViewCosts ? unitCost : null,
        unit_charge: unitCharge,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.repairs.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.repairs.detail(repairId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessories.all });
      onClose();
    },
    onError: (err: unknown) => {
      setErrorMsg(parseDatabaseError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    mutation.mutate();
  };

  return (
    <Modal isOpen={isOpen} title="Add Repair Part / Material" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        {errorMsg && <ErrorAlert message={errorMsg} />}

        <div className="flex items-center space-x-2 bg-brand-bg p-1 rounded border border-brand-border">
          <button
            type="button"
            onClick={() => setPartType('inventory')}
            className={`flex-1 py-1.5 font-semibold rounded ${
              partType === 'inventory' ? 'bg-brand-green text-white' : 'text-brand-muted'
            }`}
          >
            Inventory Accessory Part
          </button>
          <button
            type="button"
            onClick={() => setPartType('custom')}
            className={`flex-1 py-1.5 font-semibold rounded ${
              partType === 'custom' ? 'bg-brand-green text-white' : 'text-brand-muted'
            }`}
          >
            Custom Part / Outside Item
          </button>
        </div>

        {partType === 'inventory' ? (
          <div className="space-y-2">
            <label className="block font-bold text-brand-black uppercase">
              Select Inventory Accessory Stock <span className="text-brand-red">*</span>
            </label>
            <input
              placeholder="Search accessory by name or SKU..."
              value={accessorySearch}
              onChange={(e) => setAccessorySearch(e.target.value)}
              className="w-full px-2.5 py-1.5 border rounded"
            />
            <div className="max-h-36 overflow-y-auto divide-y border rounded bg-white">
              {accessories.length === 0 ? (
                <p className="p-3 text-center text-brand-muted italic">No matching active accessories</p>
              ) : (
                accessories.map((acc) => {
                  const selected = selectedAccessoryId === acc.id;
                  return (
                    <div
                      key={acc.id}
                      onClick={() => handleSelectAccessory(acc)}
                      className={`p-2 flex items-center justify-between cursor-pointer ${
                        selected ? 'bg-green-50 border-l-4 border-brand-green' : 'hover:bg-brand-bg'
                      }`}
                    >
                      <div>
                        <p className="font-semibold text-brand-black">{acc.name}</p>
                        <p className="text-[10px] text-brand-muted">SKU: {acc.sku} • In Stock: {acc.quantity}</p>
                      </div>
                      <span className="font-bold text-brand-green">Rs. {acc.selling_price.toLocaleString('en-PK')}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : (
          <div>
            <label className="block font-bold text-brand-black uppercase mb-1">
              Custom Part Description <span className="text-brand-red">*</span>
            </label>
            <input
              placeholder="e.g. Dell XPS Replacement Keyboard (Outside Vendor)..."
              value={customDescription}
              onChange={(e) => setCustomDescription(e.target.value)}
              className="w-full px-3 py-2 border rounded font-medium"
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-bold text-brand-black uppercase mb-1">Quantity</label>
            <input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
              className="w-full px-3 py-1.5 border rounded font-bold"
            />
          </div>

          <div>
            <label className="block font-bold text-brand-black uppercase mb-1">Unit Charge to Customer (PKR)</label>
            <input
              type="number"
              min={0}
              value={unitCharge}
              onChange={(e) => setUnitCharge(Number(e.target.value) || 0)}
              className="w-full px-3 py-1.5 border rounded font-bold text-brand-green"
            />
          </div>
        </div>

        {canViewCosts && (
          <div>
            <label className="block font-semibold text-brand-muted uppercase mb-1">
              Unit Cost to Shop (PKR) <span className="text-[10px] text-brand-muted">(Owner/Manager view)</span>
            </label>
            <input
              type="number"
              min={0}
              value={unitCost}
              onChange={(e) => setUnitCost(Number(e.target.value) || 0)}
              className="w-full px-3 py-1.5 border rounded bg-brand-bg font-medium"
            />
          </div>
        )}

        <div className="flex items-center justify-end space-x-3 pt-3 border-t">
          <button type="button" onClick={onClose} disabled={mutation.isPending} className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black">
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-bold rounded hover:bg-brand-green-hover disabled:opacity-50"
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            <span>Add Part to Job</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};
